import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Expense } from '@features/activity/domain/expenses.interface';
import { DateFilterDto, Filters, FiltersDto, FiltersResponse } from '@features/activity/domain/filters.interface';
import { PaymentRepository } from '@features/payments/domain/payment.repository';
import {
    DeletePaymentMethodResponse,
    PaymentMethodResponse,
    Payments,
    PaymentsResponse,
} from '@features/payments/domain/payments.interface';
import { MICROSERVICES as microservices } from '@shared/constants/microservices';
import { PaymentMethodClass, PaymentMethodFactory } from '@shared/factories/payment-method.factory';
import { SetupIntentResponse } from '@shared/models/interfaces/setup-intent-response.interface';
import { SetupIntent } from '@shared/models/interfaces/setup-intent.interface';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/internal/operators/map';

@Injectable({
    providedIn: 'root',
})
export class PaymentService extends PaymentRepository {
    readonly http = inject(HttpClient);

    private readonly months = 6; //Number of months recovery in total-expenses (graph)
    private readonly stripe_setup_intent = '/setup_intents';
    private readonly total_expenses = '/total-expenses';
    private readonly payments = '/payments';

    getSetupIntent(): Observable<SetupIntent> {
        const body = {
            paymentMethodType: 'card',
        };

        return this.http
            .post<SetupIntentResponse>(`${environment.url}${microservices.payments}${this.stripe_setup_intent}`, body)
            .pipe(map((response: SetupIntentResponse) => response.data.tokens));
    }

    getTotalExpensesByMonth(): Observable<Expense[]> {
        const params = new HttpParams().set('months', this.months);
        const options = {
            params,
        };

        return this.http
            .get<{ expenses: Expense[] }>(`${environment.url}${microservices.payments}${this.total_expenses}`, options)
            .pipe(map((response) => response.expenses.reverse()));
    }

    getPaymentList(page = 1, pageSize = 30, filtersDto: FiltersDto): Observable<PaymentsResponse> {
        let params = new HttpParams().set('page', page).set('pageSize', pageSize);
        params = this.filtersQueryBuilder(params, filtersDto);
        const options = {
            params,
        };
        return this.http.get<PaymentsResponse>(`${environment.url}${microservices.payments}${this.payments}`, options);
    }

    getDetail(paymentId: string): Observable<Payments> {
        return this.http.get<Payments>(`${environment.url}${microservices.payments}${this.payments}/${paymentId}`);
    }

    setDefaultPaymentMethod(paymentId: string) {
        const params = new HttpParams().set('pmId', paymentId);
        const options = {
            params,
        };

        return this.http.post(`${environment.url}${microservices.payments}/payment_methods/${paymentId}`, options);
    }

    getFilters(filtersDto: FiltersDto): Observable<Filters> {
        let params = new HttpParams();

        if (filtersDto.vehicleType.length > 0) {
            filtersDto.vehicleType.forEach((type) => {
                params = params.append('vehicleType', type);
            });
        }
        if (filtersDto.licensesPlate.length > 0) {
            filtersDto.licensesPlate.forEach((type) => {
                params = params.append('vehicle', type);
            });
        }
        const options = {
            params,
        };
        return this.http.get<FiltersResponse>(`${environment.url}${microservices.payments}/payment-filters`, options).pipe(
            map(({ filters }: FiltersResponse) => {
                const { vehicleTypes, vehicles } = filters;
                return {
                    vehicleTypes,
                    vehicles,
                };
            })
        );
    }

    getPaymentMethodList(): Observable<PaymentMethodClass[]> {
        return this.http
            .get<PaymentMethodResponse>(`${environment.url}${microservices.payments}/payment_methods`)
            .pipe(map((response) => PaymentMethodFactory.createPaymentMethods(response.data.payment_methods)));
    }

    deletePaymentMethod(id: string): Observable<DeletePaymentMethodResponse> {
        return this.http.delete<DeletePaymentMethodResponse>(`${environment.url}${microservices.payments}/payment_methods/${id}`);
    }

    exportPayments(filtersDto: FiltersDto, format = 'csv'): Observable<HttpResponse<Blob>> {
        let params = new HttpParams();
        params = this.filtersQueryBuilder(params, filtersDto);
        params = params.append('format', format);
        return this.http.get(`${environment.url}${microservices.payments}${this.payments}/export`, {
            params,
            observe: 'response',
            responseType: 'blob',
        });
    }

    downloadInvoicePdf(paymentId: string): Observable<HttpResponse<Blob>> {
        return this.http.get(`${environment.url}${microservices.payments}${this.payments}/${paymentId}/invoice-pdf`, {
            observe: 'response',
            responseType: 'blob',
        });
    }

    exportPaymentById(paymentId: string): Observable<HttpResponse<Blob>> {
        return this.http.get(`${environment.url}${microservices.payments}${this.payments}/${paymentId}/export`, {
            observe: 'response',
            responseType: 'blob',
        });
    }

    private filtersQueryBuilder(params: HttpParams, filtersDto: FiltersDto) {
        if (filtersDto.vehicleType.length > 0) {
            filtersDto.vehicleType.forEach((type) => {
                params = params.append('vehicleType', type);
            });
        }
        if (filtersDto.licensesPlate.length > 0) {
            filtersDto.licensesPlate.forEach((type) => {
                params = params.append('vehicle', type);
            });
        }
        if (filtersDto.dateRange.selected) {
            const range: DateFilterDto[] = [
                {
                    from: this.dateTransform(filtersDto.dateRange.range.from),
                    to: this.dateTransform(filtersDto.dateRange.range.to),
                },
            ];
            params = params.set('multipleDateFilter', JSON.stringify(range));
        }
        if (filtersDto.datePeriod.selected) {
            const range: DateFilterDto[] = [];
            filtersDto.datePeriod.years.forEach((year) => {
                filtersDto.datePeriod.months.forEach((month) => {
                    const monthDate = new Date(new Date().getFullYear(), month.numMonth + 1, 0);
                    const numDays = monthDate.getDate();
                    range.push({
                        from: `${year.label}${(month.numMonth + 1).toString().padStart(2, '0')}01`,
                        to: `${year.label}${(month.numMonth + 1).toString().padStart(2, '0')}${numDays}`,
                    });
                });
            });
            params = params.set('multipleDateFilter', JSON.stringify(range));
        }
        return params;
    }

    private dateTransform(value: string): string {
        const date = new Date(value);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const newDate = `${year}${month}${day}`;
        return newDate;
    }
}
